import { captureRemixErrorBoundaryError } from "@sentry/remix";
import {
  json,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useRouteError,
} from "@remix-run/react";
import type { LinksFunction, LoaderFunctionArgs } from "@remix-run/node";
import { Crisp } from "crisp-sdk-web";
import "./tailwind.css";
import { auth } from "./services/auth.server";
import { useEffect, useState } from "react";
import posthog from "posthog-js";
import { getUserById, User } from "db/user.server";
import TrailBanner from "./components/TrialBanner";
import NavBar from "./components/NavBar";
import stripe from "./services/stripe.server";
import Stripe from "stripe";
import { Toaster } from "components/ui/sonner";
import Footer from "./components/Footer";
import ErrorPage from "./components/ErrorPage";
import Header from "./components/Header";

export const links: LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&family=Recursive:wght@300..1000&display=swap",
  },
  {
    rel: "stylesheet",
    href: "https://cdnjs.cloudflare.com/ajax/libs/highlight.js/11.9.0/styles/default.min.css",
  },
];

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await auth.isAuthenticated(request);
  if (!user) return json({ user });

  const freshUser = await getUserById(user.id);

  let subscription = await stripe.subscriptions
    .list({
      customer: user.stripeCustomerId,
      status: "all",
    })
    .then((res) => res.data[0]);
  if (!subscription) {
    subscription = await stripe.subscriptions.create({
      customer: user.stripeCustomerId,
      items: [{ price: process.env.STRIPE_KNACKSHEET_TIER_1_PRICE_ID }],
      trial_period_days: 14,
      trial_settings: {
        end_behavior: {
          missing_payment_method: "cancel",
        },
      },
    });
  }

  return json({ user: freshUser, subscription });
}

function retryUntilTrue(checkCondition: () => boolean, callback: () => void) {
  const interval = 100;
  const intervalId = setInterval(() => {
    if (checkCondition()) {
      clearInterval(intervalId);
      callback();
    }
  }, interval);
}

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
        <Toaster
          toastOptions={{
            classNames: {
              error: "bg-red-500 border-red-700",
              success: "text-green-500 border-green-700",
              warning: "text-yellow-500 border-yellow-700",
              info: "bg-blue-500 border-blue-700",
            },
          }}
        />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  const error = useRouteError();
  captureRemixErrorBoundaryError(error);
  return <ErrorPage />;
};

export default function App() {
  const { pathname } = useLocation();
  const { user, subscription } = useLoaderData<{
    user: User;
    subscription: Stripe.Subscription;
  }>();

  const [isTrialBannerOpen, setIsTrialBannerOpen] = useState(
    subscription?.status === "trialing",
  );

  useEffect(() => Crisp.configure(import.meta.env.VITE_CRISP_WEBSITE_ID), []);

  useEffect(() => {
    if (import.meta.env.MODE === "production")
      retryUntilTrue(
        () => {
          return posthog.__loaded;
        },
        () => {
          if (!user?.id) {
            posthog.reset();
            posthog.set_config({
              autocapture: false,
              disable_session_recording: true,
              capture_pageview: false,
            });
            posthog.opt_out_capturing();
          } else {
            posthog.identify(user.id, { email: user.email });
            posthog.set_config({
              autocapture: true,
              disable_session_recording: false,
              capture_pageview: true,
            });
            posthog.opt_in_capturing();
          }
        },
      );

    if (user?.id) {
      Crisp.user.setEmail(user.email);
      Crisp.session.setData({
        user_id: user.id,
      });
    }
  }, [user?.id, user?.email]);

  return (
    <>
      {user?.id && pathname !== "/" && (
        <>
          <div className="fixed top-0 z-20 flex w-full flex-col">
            <NavBar
              isTrialBannerOpen={isTrialBannerOpen}
              isTrialing={subscription.status === "trialing"}
            />
          </div>
          <TrailBanner
            trialEnd={subscription.trial_end}
            isOpen={isTrialBannerOpen}
            setIsOpen={setIsTrialBannerOpen}
            hasShownSurvey={user.hasShownSurvey}
          />
        </>
      )}
      {pathname !== "/login" && !user?.id && <Header isLoggedIn={!!user} />}
      <Outlet />
      <Footer />
    </>
  );
}
